import { z } from 'zod';
// =========================================================
// Schemas Zod del endpoint POST /api/batches/ingestar.
//
// Convención de naming: snake_case (matchea las columnas SQL del esquema
// `hor` para evitar transformaciones intermedias). Es la excepción frente
// al resto de schemas de `hormigon.schema.ts` que usan camelCase porque
// son inputs de pantallas; estos schemas son contratos de ingesta crudos.
// =========================================================
/**
 * Request del endpoint de ingesta de CSV de planta Blend.
 *
 * - `contenido_csv` viaja como UTF-8 plano (NO base64). Los CSVs de Blend
 *   pesan ~5MB, perfectamente manejables en el body default de Functions.
 * - `forzar_reingesta` desactiva la deduplicación por hash. Útil cuando un
 *   archivo ya cargado se quiere reprocesar tras un cambio en el parser o
 *   datos maestros.
 */
export const IngestaBlendRequestSchema = z.object({
    nombre_archivo: z.string().min(1).max(255),
    contenido_csv: z.string().min(1),
    forzar_reingesta: z.boolean().default(false),
});
/**
 * Error de parseo de una fila individual del CSV.
 * `fila` es 1-indexed (header = fila 1, primer batch = fila 2).
 */
export const ErrorIngestaSchema = z.object({
    fila: z.number().int(),
    campo: z.string().optional(),
    mensaje: z.string(),
});
/**
 * Warning emitido por el agrupador automático cuando un batch nuevo
 * matchea (id_planta, id_receta_blend, destino_raw, día) contra una
 * colada existente que ya NO es modificable. En ese caso se crea
 * una colada nueva en 'sugerida' y el operador debe resolver
 * manualmente (merge/split/mover).
 */
export const ColadaWarningSchema = z.object({
    tipo: z.literal('batch_matchea_colada_no_modificable'),
    id_batch: z.number().int(),
    id_colada_existente: z.number().int(),
    codigo_interno_existente: z.number().int(),
    estado_existente: z.enum(['confirmada', 'digitada', 'cerrada', 'anulada']),
    id_colada_nueva: z.number().int(),
    codigo_interno_nueva: z.number().int(),
    mensaje: z.string(),
});
/**
 * Resumen de la agrupación automática que corre dentro de la transacción
 * de ingesta, después de insertar los batches y antes del COMMIT.
 */
export const AgrupacionIngestaSchema = z.object({
    coladasCreadas: z.number().int().nonnegative(),
    coladasActualizadas: z.number().int().nonnegative(),
    batchesAgrupados: z.number().int().nonnegative(),
    warnings: z.array(ColadaWarningSchema),
});
/**
 * Response del endpoint.
 *
 * Estados posibles:
 *  - `'ok'`: todos los batches insertados sin errores.
 *  - `'parcial'`: al menos un batch insertado pero hubo errores de parseo
 *    o batches omitidos por duplicación previa.
 *  - `'duplicado_archivo'`: el hash del archivo ya existía y no se forzó
 *    reingesta. No se insertó ni se modificó nada.
 */
export const IngestaBlendResponseSchema = z.object({
    id_importacion: z.number().int(),
    estado: z.enum(['ok', 'duplicado_archivo', 'parcial']),
    hash_archivo: z.string().length(64),
    resumen: z.object({
        filas_recibidas: z.number().int(),
        batches_insertados: z.number().int(),
        batches_omitidos_duplicado: z.number().int(),
        filas_con_error: z.number().int(),
        /** Lista de `Machine_SN` (serial Blend) únicos encontrados en el CSV. */
        plantas: z.array(z.string()),
        /** ISO UTC, o null si el CSV no tenía filas parseables. */
        fecha_min: z.string().nullable(),
        fecha_max: z.string().nullable(),
    }),
    errores: z.array(ErrorIngestaSchema),
    /**
     * Resultado de la agrupación automática que corrió dentro de la misma
     * transacción de ingesta. Si estado === 'duplicado_archivo', viene todo
     * en cero (no se agruparon batches porque no se insertaron). Si estado
     * === 'parcial' y no se insertó ningún batch, también viene en cero.
     */
    agrupacion: AgrupacionIngestaSchema,
});
//# sourceMappingURL=ingesta-blend.js.map